import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { App as AntApp, ConfigProvider, theme as antdTheme } from 'antd';
import { theme } from './styles/theme';
import { GlobalStyles } from './styles/GlobalStyles';
import { AuthProvider } from './contexts/AuthContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { SignInPage, SignUpPage, ForgotPasswordPage, SetPasswordPage } from './pages/Auth';
import LandingPage from './pages/Home/LandingPage';
import HomePage from './pages/Home/HomePage';
import SettingsPage from './pages/Settings';
import DocsPage from './pages/Docs';
import PairTrading from './pages/PairTrading';
import RegisterPortfolio from './pages/RegisterPortfolio';
import ComingSoon from './pages/ComingSoon';
import GridTrading from './pages/GridTrading';
import SmartBeta from './pages/SmartBeta';
import ExecutionsManagement from './pages/ExecutionsManagement';
import PortfolioPage from './pages/Portfolio';
import TournamentPage from './pages/Tournament';
import AiAssistantPage from './pages/AiAssistant';
import PriceBoardPage from './pages/PriceBoard';
import MainLayout from './components/MainLayout';
import SupportWidget from './components/SupportWidget';
import { SupportProvider } from './components/SupportWidget/SupportProvider';

// Component to handle root path redirect
const RootRedirect: React.FC = () => {
  const token = localStorage.getItem('access_token');
  return token ? <Navigate to="/home" replace /> : <LandingPage />;
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: theme.colors.trading.buy,
            colorText: theme.colors.text.primary,
            colorTextSecondary: theme.colors.text.secondary,
            colorBgBase: theme.colors.background.primary,
            colorTextBase: theme.colors.text.primary,
          },
          components: {
            // One look for every table: no slab of its own colour, a quiet header and hairline
            // rows on whatever surface the table sits on.
            Table: {
              colorBgContainer: 'transparent',
              headerBg: 'transparent',
              headerColor: theme.colors.text.secondary,
              headerSplitColor: 'transparent',
              headerBorderRadius: 0,
              borderColor: 'rgba(255, 255, 255, 0.06)',
              rowHoverBg: 'rgba(255, 255, 255, 0.03)',
              rowSelectedBg: 'rgba(14, 203, 129, 0.08)',
              rowSelectedHoverBg: 'rgba(14, 203, 129, 0.12)',
              rowExpandedBg: 'transparent',
              footerBg: 'transparent',
              bodySortBg: 'rgba(255, 255, 255, 0.02)',
              headerSortActiveBg: 'transparent',
              headerSortHoverBg: 'transparent',
              fixedHeaderSortActiveBg: theme.colors.background.primary,
              headerFilterHoverBg: 'transparent',
              stickyScrollBarBg: theme.colors.background.tertiary,
              cellPaddingBlockSM: 10,
              cellPaddingInlineSM: 12,
            },
            Tabs: {
              itemColor: theme.colors.text.secondary,
              itemHoverColor: theme.colors.text.primary,
              itemActiveColor: theme.colors.trading.buy,
              itemSelectedColor: theme.colors.trading.buy,
              inkBarColor: theme.colors.trading.buy,
            },
            Input: {
              activeBorderColor: theme.colors.border.focus,
              hoverBorderColor: theme.colors.border.focus,
            },
            InputNumber: {
              activeBorderColor: theme.colors.border.focus,
              hoverBorderColor: theme.colors.border.focus,
            },
            Select: {
              activeBorderColor: theme.colors.border.focus,
              hoverBorderColor: theme.colors.border.focus,
              // The dark algorithm derives the selected option's background from
              // colorPrimary (trading green), which lands as a moss-green block in
              // every dropdown. Use the same neutral fill as hover — the bold text
              // already says which option is selected.
              optionSelectedBg: 'rgba(255, 255, 255, 0.08)',
            },
          },
          algorithm: antdTheme.darkAlgorithm,
        }}
      >
        <GlobalStyles theme={theme} />
        <AntApp>
        <AuthProvider>
        <SidebarProvider>
        <BrowserRouter>
        <SupportProvider>
          <Routes>
            {/* Auth routes without Layout */}
            <Route path="/signin" element={<SignInPage />} />
            <Route path="/signup" element={<SignUpPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/set-password" element={<SetPasswordPage />} />

            {/* Public routes */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/register-portfolio" element={<RegisterPortfolio />} />
            <Route path="/coming-soon" element={<ComingSoon />} />

            {/* Main routes with shared Sidebar layout */}
            <Route element={<MainLayout />}>
              {/* the strategy marketplace is gone; old links land on the price board */}
              <Route path="/home" element={<HomePage />} />
              <Route path="/pair-trading" element={<PairTrading />} />
              <Route path="/price-board" element={<PriceBoardPage />} />
              <Route path="/grid-trading" element={<GridTrading />} />
              <Route path="/smart-beta" element={<SmartBeta />} />
              <Route path="/executions" element={<ExecutionsManagement />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/tournament" element={<TournamentPage />} />
              <Route path="/ai" element={<AiAssistantPage />} />
              <Route path="/docs" element={<DocsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/desktop" element={<Navigate to="/settings?tab=desktop" replace />} />
            </Route>
            <Route path="*" element={<div>404 Not Found</div>} />
          </Routes>
          {/* Bottom-right on every signed-in screen; hides itself on the auth pages. The
              provider also backs the sidebar entry, so both open the same panel. */}
          <SupportWidget />
        </SupportProvider>
        </BrowserRouter>
        </SidebarProvider>
        </AuthProvider>
        </AntApp>
      </ConfigProvider>
    </ThemeProvider>
  );
};


export default App;

